import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Inject,
  Input,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LDClientSDK } from '@app/shared/services/ldclientsdk.token';
import { LDClient } from 'launchdarkly-js-client-sdk';
import { CookieLawService } from './services/cookie-law.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * A banner component that shows a message asking users to accept cookies as required by law.
 */
@Component({
  selector: 'dgx-cookie-banner',
  templateUrl: './cookie-banner.component.html',
  styleUrls: ['./cookie-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookieBannerComponent implements OnInit {
  @Input() userHasAcceptedPrivacyPolicy: boolean = false;

  public isShown: boolean = false;

  // translations
  public trustedMessage: SafeHtml;
  public acceptCookies: string;

  // LD flags
  public privacyPolicyUpdateEnabled: boolean;

  constructor(
    private cookieLawService: CookieLawService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    @Inject(LDClientSDK) private ldClientSDK: LDClient
  ) {}

  ngOnInit(): void {
    this.privacyPolicyUpdateEnabled = this.ldClientSDK.variation(
      'privacy-policy-update-v-7-0-rollout',
      false
    );

    this.setTranslations();
    this.setupCookieRules();

    const hasAcceptedPrivacyPolicyCookie =
      this.cookieLawService.getAcceptedPrivacyPolicyCookie() === 'true';

    if (
      hasAcceptedPrivacyPolicyCookie &&
      this.cookieLawService.getAuthUser() &&
      !this.userHasAcceptedPrivacyPolicy
    ) {
      // Now logged in user, accepted while logged out, has cookie, needing to add to db.
      this.cookieLawService.setPrivacyPolicyAccepted();
      this.isShown = false;
    } else if (
      this.userHasAcceptedPrivacyPolicy ||
      (!this.cookieLawService.getAuthUser() && hasAcceptedPrivacyPolicyCookie)
    ) {
      // User accepted, cookie is stored
      this.isShown = false;
    } else {
      this.isShown = true;
    }
  }

  public setSessionCookie() {
    const sessionDate = new Date(
      Date.now() + CookieLawService.EXPIRATION_SECONDS
    );

    if (this.cookieLawService.getEuCookie() === 'true') {
      this.cookieLawService.cookieService.set(
        this.cookieLawService.getAcceptedCookieName(),
        'true',
        sessionDate,
        '/'
      );
    }
    this.cookieLawService.cookieService.set(
      this.cookieLawService.getAcceptedPrivacyPolicyCookieName(),
      'true',
      sessionDate,

      '/'
    );

    if (
      this.cookieLawService.getAuthUser() &&
      this.cookieLawService.getAcceptedPrivacyPolicyCookie() === 'true'
    ) {
      // User is logged in while accepting banner, and we for sure have the cookie.
      this.cookieLawService.setPrivacyPolicyAccepted();
    }
    this.isShown = false;
  }

  private setTranslations() {
    const messageTranslationResource = this.privacyPolicyUpdateEnabled
      ? 'dgCookieBanner_message_V2'
      : 'dgCookieBanner_message';
    this.trustedMessage = this.sanitizer.bypassSecurityTrustHtml(
      this.translate.instant(messageTranslationResource)
    );
    this.acceptCookies = this.translate.instant('dgCookieBanner_accept');
  }

  private setupCookieRules() {
    // Should call once for customers, to minimize calls wither either an uncessary call, or 403 for EU.
    if (!this.cookieLawService.shouldGetCookieScript()) {
      return;
    }

    this.cookieLawService.getCookieScript().subscribe(
      () => {
        const hasAcceptedCookieRules =
          this.cookieLawService.getAcceptedCookie() === 'true';

        if (hasAcceptedCookieRules) {
          this.isShown = false;
          return false;
        } else {
          if (this.cookieLawService.getEuCookie() === 'true') {
            this.isShown = true;
            return true;
          }
        }
      },
      () => {}
    );
  }
}
